import React, { useState } from "react";
import axios from "axios";
import logo from "../assets/lna.png";

const Membership = () => {
  const [formData, setFormData] = useState({
    title: "", name: "", email: "", gender: "", maritalStatus: "", dateOfBirth: "",
    idNumber: "", phoneNumber: "", nationality: "", employmentNumber: "", grade: "", employerName: "",
    dutyStation: "", district: "", qualifications: [""], yearObtained: [""], nextOfKinFullName: "",
    nextOfKinAddress: "", nextOfKinContact: "", nextOfKinRelationship: "", typeOfEmployment: "", paymentFrequency: "",
    paymentMethod: "", agreeToTerms: false,
  });

  const [activeSection, setActiveSection] = useState("overview");
  const [touchedFields, setTouchedFields] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const membershipTypes = [
    {
      type: "Full Member",
      eligibility: "Registered nurses, nursing assistants and specialists.",
      benefits: "Voting rights, executive eligibility, and access to training programs.",
      color: "#1e40af",
      icon: "⭐"
    },
    {
      type: "Associate Member",
      eligibility: "Student nurses, and retired nurses",
      benefits: "Participation in workshops, research, and networking events.",
      color: "#d97706",
      icon: "👥"
    },
    {
      type: "Honorary Member",
      eligibility: "Granted by the association for exceptional contributions to the nursing profession.",
      benefits: "Recognition and participation in honorary events.",
      color: "#059669",
      icon: "🏆"
    },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleBlur = (field) => {
    setTouchedFields(prev => ({ ...prev, [field]: true }));
  };

  const handleQualificationChange = (index, field, value) => {
    const updatedArray = [...formData[field]];
    updatedArray[index] = value;
    setFormData({ ...formData, [field]: updatedArray });
  };

  const addQualificationField = () => {
    setFormData({
      ...formData,
      qualifications: [...formData.qualifications, ""],
      yearObtained: [...formData.yearObtained, ""],
    });
  };

  const removeQualificationField = (index) => {
    if (formData.qualifications.length > 1) {
      const updatedQualifications = [...formData.qualifications];
      const updatedYears = [...formData.yearObtained];
      updatedQualifications.splice(index, 1);
      updatedYears.splice(index, 1);
      setFormData({
        ...formData,
        qualifications: updatedQualifications,
        yearObtained: updatedYears,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.agreeToTerms) {
      alert("You must agree to the terms and conditions to submit the form.");
      setIsSubmitting(false);
      return;
    }

    try {
      // UPDATED: Use PHP proxy for registration
      const response = await axios.post(
        "/proxy.php/backend/api/members/register",
        {
          ...formData,
          submittedAt: new Date().toISOString()
        },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 10000
        }
      );

      if (response.status === 200) {
        alert(`Thank you, ${formData.name}, for registering! Your application has been submitted successfully and is pending approval. You will receive a confirmation email once approved.`);
        // Reset form after success
        setFormData({
          title: "", name: "", email: "", gender: "", maritalStatus: "",
          dateOfBirth: "", idNumber: "", phoneNumber: "", nationality: "", employmentNumber: "", grade: "",
          employerName: "", dutyStation: "", district: "", qualifications: [""], yearObtained: [""],
          nextOfKinFullName: "", nextOfKinAddress: "", nextOfKinContact: "", nextOfKinRelationship: "",
          typeOfEmployment: "", paymentFrequency: "", paymentMethod: "", agreeToTerms: false,
        });
        setTouchedFields({});
        // Reset to overview section after successful submission
        setActiveSection("overview");
      } else {
        alert("Registration failed: " + (response.data?.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Registration error:", err);
      if (err.response?.status === 400) {
        alert("Please check your information and try again.");
      } else if (err.response?.status === 500) {
        alert("Server error. Please try again later or contact support.");
      } else {
        alert("Failed to submit form. Please check your connection and try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mobile detection helper
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div style={styles.container}>
      {/* Header Section */}
      <div style={styles.headerSection}>
        <div style={styles.headerContent}>
          <img src={logo} alt="Lesotho Nurses Association Logo" style={styles.logo} />
          <div style={styles.headerText}>
            <h1 style={styles.header}>Professional Membership Registration</h1>
            <p style={styles.subtitle}>Join the Lesotho Nursing and Midwifery Organization today</p>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobile ? (
        <div style={styles.mobileNav}>
          <select 
            value={activeSection} 
            onChange={(e) => setActiveSection(e.target.value)}
            style={styles.mobileNavSelect}
          >
            <option value="overview">📋 Overview</option>
            <option value="benefits">🎁 Benefits</option>
            <option value="types">👥 Membership Types</option>
            <option value="register">📝 Register Now</option>
          </select>
        </div>
      ) : (
        /* Desktop Navigation */
        <div style={styles.navigation}>
          {["overview", "benefits", "types", "register"].map((section) => (
            <button 
              key={section}
              style={{
                ...styles.navButton,
                ...(activeSection === section ? styles.activeNavButton : {})
              }}
              onClick={() => setActiveSection(section)}
            >
              <span style={styles.navIcon}>
                {section === "overview" ? "📋" : 
                 section === "benefits" ? "🎁" :
                 section === "types" ? "👥" : "📝"}
              </span>
              {section === "overview" ? "Overview" :
               section === "benefits" ? "Benefits" :
               section === "types" ? "Types" : "Register"}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div style={styles.content}>
        {(activeSection === "overview" || activeSection === "all") && (
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Why Join LeNMO?</h2>
              <div style={styles.titleUnderline}></div>
            </div>
            <p style={styles.text}>
              Joining the Lesotho Nursing and Midwifery Organization gives you access to professional development programs, 
              leadership opportunities, and a supportive network of healthcare professionals dedicated to advancing nursing excellence.
            </p>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <div style={styles.featureIcon}>📚</div>
                <h3 style={styles.featureTitle}>Professional Development</h3>
                <p style={styles.featureText}>Access to continuous learning and career advancement opportunities</p>
              </div>
              <div style={styles.featureCard}>
                <div style={styles.featureIcon}>🤝</div>
                <h3 style={styles.featureTitle}>Networking</h3>
                <p style={styles.featureText}>Connect with peers and leaders in the nursing profession</p>
              </div>
              <div style={styles.featureCard}>
                <div style={styles.featureIcon}>🛡️</div>
                <h3 style={styles.featureTitle}>Advocacy</h3>
                <p style={styles.featureText}>Collective representation for better working conditions</p>
              </div>
            </div>
          </section>
        )}

        {(activeSection === "benefits" || activeSection === "all") && (
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Member Benefits</h2>
              <div style={styles.titleUnderline}></div>
            </div>
            <div style={styles.benefitsGrid}>
              {[
                { icon: "💼", text: "Collective Bargaining: Members benefit from negotiations for better working conditions, salaries, and benefits." },
                { icon: "⚖️", text: "Representation During Disciplinary Process: Legal and professional support during disciplinary actions." },
                { icon: "🛡️", text: "Professional Misconduct Support: Assistance in disputes and ethical concerns." },
                { icon: "📰", text: "Mooki Newsletter: Stay updated with news and association activities." },
                { icon: "🛒", text: "Merchandise Discounts: Exclusive discounts on supplies and gear." },
                { icon: "🎓", text: "CPD Training Discounts: Pay less for professional development training." },
                { icon: "🎁", text: "Annual Hamper: Appreciation gift package." },
                { icon: "💰", text: "Loans at 5% Interest: Access financial support." },
                { icon: "📚", text: "Professional Resources: Access journals and guidelines." },
                { icon: "🏫", text: "Scholarships: Educational funding opportunities." }
              ].map((benefit, index) => (
                <div key={index} style={styles.benefitItem}>
                  <div style={styles.benefitIcon}>{benefit.icon}</div>
                  <div style={styles.benefitText}>{benefit.text}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {(activeSection === "types" || activeSection === "all") && (
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Membership Types</h2>
              <div style={styles.titleUnderline}></div>
            </div>
            <p style={styles.text}>
              Membership is voluntary and open to all nursing cadres under the Nursing and Midwifery Act:
            </p>
            <div style={styles.membershipTypes}>
              {membershipTypes.map((type, index) => (
                <div key={index} style={styles.membershipCard}>
                  <div style={{...styles.membershipHeader, background: type.color}}>
                    <div style={styles.membershipIcon}>{type.icon}</div>
                    <h3 style={styles.membershipTitle}>{type.type}</h3>
                  </div>
                  <div style={styles.membershipBody}>
                    <div style={styles.membershipDetail}>
                      <strong>Eligibility:</strong> {type.eligibility}
                    </div>
                    <div style={styles.membershipDetail}>
                      <strong>Benefits:</strong> {type.benefits}
                    </div>
                    <div style={styles.membershipHighlight}>
                      Perfect for {type.type.toLowerCase()}s
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {(activeSection === "register" || activeSection === "all") && (
          <section style={styles.formSection}>
            <div style={styles.formHeader}>
              <div style={styles.formHeaderContent}>
                <h2 style={styles.formMainTitle}>Membership Application Form</h2>
                <p style={styles.formSubtitle}>Complete all sections below to apply for membership</p>
              </div>
            </div>

            <div style={styles.formContainer}>
              <form onSubmit={handleSubmit} style={styles.form}>
                {/* Personal Information */}
                <div style={styles.formSectionCard}>
                  <div style={styles.sectionHeaderWithIcon}>
                    <div style={styles.sectionIcon}>👤</div>
                    <div>
                      <h3 style={styles.formSectionTitle}>Personal Information</h3>
                      <p style={styles.formSectionSubtitle}>Basic details about yourself</p>
                    </div>
                  </div>

                  <div style={styles.formGrid}>
                    {[
                      { id: "title", label: "Title *", type: "select", options: ["Select Title", "Miss", "Mrs", "Mr", "Dr"], icon: "👑" },
                      { id: "name", label: "Full Name *", type: "text", placeholder: "Enter your full name", icon: "📝" },
                      { id: "gender", label: "Gender *", type: "select", options: ["Select Gender", "Male", "Female"], icon: "⚧" },
                      { id: "maritalStatus", label: "Marital Status *", type: "select", options: ["Select Status", "Single", "Married", "Widowed", "Divorced"], icon: "💍" },
                      { id: "dateOfBirth", label: "Date of Birth *", type: "date", icon: "📅" },
                      { id: "idNumber", label: "ID Number *", type: "text", placeholder: "Enter your ID number", icon: "🆔" },
                      { id: "nationality", label: "Nationality *", type: "text", placeholder: "Enter your nationality", icon: "🌍" }
                    ].map((field) => (
                      <div key={field.id} style={styles.formGroup}>
                        <label style={styles.label} htmlFor={field.id}>
                          {field.label}
                        </label>
                        <div style={styles.inputContainer}>
                          {field.type === "select" ? (
                            <select 
                              id={field.id}
                              name={field.id} 
                              value={formData[field.id]} 
                              onChange={handleChange}
                              onBlur={() => handleBlur(field.id)}
                              required 
                              style={{
                                ...styles.select,
                                ...(touchedFields[field.id] && !formData[field.id] ? styles.inputError : {}),
                                ...(formData[field.id] ? styles.inputSuccess : {})
                              }}
                            >
                              {field.options.map((option, idx) => (
                                <option key={idx} value={idx === 0 ? "" : option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input 
                              type={field.type} 
                              id={field.id}
                              name={field.id} 
                              value={formData[field.id]} 
                              onChange={handleChange}
                              onBlur={() => handleBlur(field.id)}
                              required 
                              style={{
                                ...styles.input,
                                ...(touchedFields[field.id] && !formData[field.id] ? styles.inputError : {}),
                                ...(formData[field.id] ? styles.inputSuccess : {})
                              }}
                              placeholder={field.placeholder} 
                            />
                          )}
                          <div style={styles.inputIcon}>{field.icon}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact & Employment Information */}
                <div style={styles.formSectionCard}>
                  <div style={styles.sectionHeaderWithIcon}>
                    <div style={styles.sectionIcon}>💼</div>
                    <div>
                      <h3 style={styles.formSectionTitle}>Contact & Employment Details</h3>
                      <p style={styles.formSectionSubtitle}>Your professional information</p>
                    </div>
                  </div>

                  <div style={styles.formGrid}>
                    {[
                      { id: "email", label: "Email Address *", type: "email", placeholder: "Enter your email", icon: "✉️" },
                      { id: "phoneNumber", label: "Phone Number *", type: "text", placeholder: "Enter your phone number", icon: "📱" },
                      { id: "employmentNumber", label: "Employment Number *", type: "text", placeholder: "Enter employment number", icon: "🔢" },
                      { id: "grade", label: "Grade *", type: "text", placeholder: "Enter your grade", icon: "📊" },
                      { id: "employerName", label: "Employer Name *", type: "text", placeholder: "Enter employer's name", icon: "🏢" },
                      { id: "typeOfEmployment", label: "Employment Type *", type: "select", options: ["Select Type", "Permanent and pensionable", "Contract"], icon: "📄" },
                      { id: "dutyStation", label: "Duty Station *", type: "text", placeholder: "Enter duty station", icon: "📍" },
                      { id: "district", label: "District *", type: "text", placeholder: "Enter your district", icon: "🗺️" }
                    ].map((field) => (
                      <div key={field.id} style={styles.formGroup}>
                        <label style={styles.label} htmlFor={field.id}>
                          {field.label}
                        </label>
                        <div style={styles.inputContainer}>
                          {field.type === "select" ? (
                            <select 
                              id={field.id}
                              name={field.id} 
                              value={formData[field.id]} 
                              onChange={handleChange}
                              onBlur={() => handleBlur(field.id)}
                              required 
                              style={{
                                ...styles.select,
                                ...(touchedFields[field.id] && !formData[field.id] ? styles.inputError : {}),
                                ...(formData[field.id] ? styles.inputSuccess : {})
                              }}
                            >
                              {field.options.map((option, idx) => (
                                <option key={idx} value={idx === 0 ? "" : option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input 
                              type={field.type} 
                              id={field.id}
                              name={field.id} 
                              value={formData[field.id]} 
                              onChange={handleChange}
                              onBlur={() => handleBlur(field.id)}
                              required 
                              style={{
                                ...styles.input,
                                ...(touchedFields[field.id] && !formData[field.id] ? styles.inputError : {}),
                                ...(formData[field.id] ? styles.inputSuccess : {})
                              }}
                              placeholder={field.placeholder} 
                            />
                          )}
                          <div style={styles.inputIcon}>{field.icon}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Qualifications */}
                <div style={styles.formSectionCard}>
                  <div style={styles.sectionHeaderWithIcon}>
                    <div style={styles.sectionIcon}>🎓</div>
                    <div>
                      <h3 style={styles.formSectionTitle}>Qualifications</h3>
                      <p style={styles.formSectionSubtitle}>Your educational background and achievements</p>
                    </div>
                  </div>

                  {formData.qualifications.map((qualification, index) => (
                    <div key={index} style={styles.qualificationRow}>
                      <div style={styles.qualificationGroup}>
                        <label style={styles.label}>
                          Qualification {index + 1} *
                        </label>
                        <input
                          type="text"
                          value={qualification}
                          onChange={(e) => handleQualificationChange(index, "qualifications", e.target.value)}
                          style={styles.input}
                          required
                          placeholder="e.g., Bachelor of Science in Nursing"
                        />
                      </div>
                      <div style={styles.qualificationGroup}>
                        <label style={styles.label}>
                          Year Obtained *
                        </label>
                        <input
                          type="text"
                          value={formData.yearObtained[index]}
                          onChange={(e) => handleQualificationChange(index, "yearObtained", e.target.value)}
                          style={styles.input}
                          required
                          placeholder="e.g., 2020"
                        />
                      </div>
                      {formData.qualifications.length > 1 && (
                        <button 
                          type="button" 
                          onClick={() => removeQualificationField(index)} 
                          style={styles.removeButton}
                          title="Remove qualification"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  
                  <button 
                    type="button" 
                    onClick={addQualificationField} 
                    style={styles.addButton}
                  >
                    <span style={styles.addButtonIcon}>+</span>
                    Add Another Qualification
                  </button>
                </div>

                {/* Next of Kin */}
                <div style={styles.formSectionCard}>
                  <div style={styles.sectionHeaderWithIcon}>
                    <div style={styles.sectionIcon}>👨‍👩‍👧‍👦</div>
                    <div>
                      <h3 style={styles.formSectionTitle}>Next of Kin Information</h3>
                      <p style={styles.formSectionSubtitle}>Emergency contact details</p>
                    </div>
                  </div>

                  <div style={styles.formGrid}>
                    {[
                      { id: "nextOfKinFullName", label: "Full Name *", type: "text", placeholder: "Enter full name", icon: "👤" },
                      { id: "nextOfKinRelationship", label: "Relationship *", type: "text", placeholder: "e.g., Spouse, Parent", icon: "💑" },
                      { id: "nextOfKinContact", label: "Contact Number *", type: "text", placeholder: "Enter contact number", icon: "📞" },
                      { id: "nextOfKinAddress", label: "Address *", type: "text", placeholder: "Enter full address", icon: "🏠" }
                    ].map((field) => (
                      <div key={field.id} style={styles.formGroup}>
                        <label style={styles.label} htmlFor={field.id}>
                          {field.label}
                        </label>
                        <div style={styles.inputContainer}>
                          <input 
                            type={field.type} 
                            id={field.id}
                            name={field.id} 
                            value={formData[field.id]} 
                            onChange={handleChange}
                            onBlur={() => handleBlur(field.id)}
                            required 
                            style={{
                              ...styles.input,
                              ...(touchedFields[field.id] && !formData[field.id] ? styles.inputError : {}),
                              ...(formData[field.id] ? styles.inputSuccess : {})
                            }}
                            placeholder={field.placeholder} 
                          />
                          <div style={styles.inputIcon}>{field.icon}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Payment Information */}
                <div style={styles.formSectionCard}>
                  <div style={styles.sectionHeaderWithIcon}>
                    <div style={styles.sectionIcon}>💳</div>
                    <div>
                      <h3 style={styles.formSectionTitle}>Payment Information</h3>
                      <p style={styles.formSectionSubtitle}>Select your preferred payment method</p>
                    </div>
                  </div>

                  <div style={styles.formGrid}>
                    {[
                      { id: "paymentFrequency", label: "Payment Frequency *", type: "select", options: ["Select Frequency", "Monthly - M50", "Annual - M600"], icon: "🔄" },
                      { id: "paymentMethod", label: "Payment Method *", type: "select", options: ["Select Method", "Automatic salary deduction", "Monthly debit order", "Annual Lumpsum"], icon: "💵" }
                    ].map((field) => (
                      <div key={field.id} style={styles.formGroup}>
                        <label style={styles.label} htmlFor={field.id}>
                          {field.label}
                        </label>
                        <div style={styles.inputContainer}>
                          <select 
                            id={field.id}
                            name={field.id} 
                            value={formData[field.id]} 
                            onChange={handleChange}
                            onBlur={() => handleBlur(field.id)}
                            required 
                            style={{
                              ...styles.select,
                              ...(touchedFields[field.id] && !formData[field.id] ? styles.inputError : {}),
                              ...(formData[field.id] ? styles.inputSuccess : {})
                            }}
                          >
                            {field.options.map((option, idx) => (
                              <option key={idx} value={idx === 0 ? "" : option}>
                                {option}
                              </option>
                            ))}
                          </select>
                          <div style={styles.inputIcon}>{field.icon}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Terms and Submit */}
                <div style={styles.formSectionCard}>
                  <div style={styles.termsSection}>
                    <div style={styles.termsHeader}>
                      <h3 style={styles.termsTitle}>Terms & Conditions</h3>
                      <p style={styles.termsSubtitle}>Please read and accept the terms to complete your application</p>
                    </div>
                    
                    <label style={styles.termsLabel}>
                      <input
                        type="checkbox"
                        name="agreeToTerms"
                        checked={formData.agreeToTerms}
                        onChange={handleChange}
                        required
                        style={styles.checkbox}
                      />
                      <span style={{
                        ...styles.checkboxCustom,
                        ...(formData.agreeToTerms ? styles.checkboxChecked : {})
                      }}></span>
                      <span style={styles.termsText}>
                        By clicking here, I confirm that the information provided is correct and agrees to terms and conditions of LeNMO membership as determined from time to time by congress.
                      </span>
                    </label>
                  </div>

                  <div style={styles.submitSection}>
                    <button 
                      type="submit" 
                      style={{
                        ...styles.submitButton,
                        ...(isSubmitting ? styles.submitButtonDisabled : {})
                      }}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <div style={styles.loadingSpinner}></div>
                      ) : (
                        <>
                          <span style={styles.submitButtonText}>Submit Application</span>
                          <span style={styles.submitButtonIcon}>🚀</span>
                        </>
                      )}
                    </button>
                    <p style={styles.formNote}>
                      By submitting this form, you agree to our privacy policy and terms of service.
                    </p>
                  </div>
                </div>
              </form>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    fontFamily: "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    lineHeight: 1.6,
    color: "#333",
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
  },
  headerSection: {
    background: "linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)",
    color: "white",
    padding: "2rem 1rem",
    textAlign: "center",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
  headerContent: {
    maxWidth: "1200px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "1rem",
  },
  logo: {
    width: "80px",
    height: "80px",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    objectFit: "contain",
  },
  headerText: {
    flex: 1,
  },
  header: {
    fontSize: "clamp(1.8rem, 4vw, 2.5rem)",
    fontWeight: "700",
    marginBottom: "0.5rem",
    textShadow: "0 2px 4px rgba(0,0,0,0.3)",
  },
  subtitle: {
    fontSize: "clamp(1rem, 2.5vw, 1.2rem)",
    opacity: 0.9,
    maxWidth: "600px",
    margin: "0 auto",
    fontWeight: "300",
  },
  navigation: {
    display: "flex",
    justifyContent: "center",
    backgroundColor: "white",
    boxShadow: "0 2px 15px rgba(0,0,0,0.08)",
    position: "sticky",
    top: 0,
    zIndex: 100,
    borderBottom: "1px solid #e2e8f0",
    overflowX: "auto",
  },
  navButton: {
    padding: "1rem 1.5rem",
    backgroundColor: "transparent",
    border: "none",
    fontSize: "0.95rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    color: "#64748b",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    borderBottom: "3px solid transparent",
    whiteSpace: "nowrap",
    minWidth: "fit-content",
  },
  navIcon: {
    fontSize: "1.1rem",
  },
  activeNavButton: {
    color: "#1e40af",
    borderBottom: "3px solid #1e40af",
    backgroundColor: "#f8fafc",
  },
  mobileNav: {
    backgroundColor: "white",
    padding: "1rem",
    borderBottom: "1px solid #e2e8f0",
  },
  mobileNavSelect: {
    width: "100%",
    padding: "0.75rem",
    border: "2px solid #e2e8f0",
    borderRadius: "8px",
    fontSize: "1rem",
    backgroundColor: "white",
  },
  content: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "2rem 1rem",
  },
  section: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "2rem",
    marginBottom: "2rem",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.04)",
    border: "1px solid #f1f5f9",
  },
  sectionHeader: {
    marginBottom: "2rem",
    textAlign: "center",
  },
  sectionTitle: {
    fontSize: "clamp(1.5rem, 3vw, 2rem)",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "0.75rem",
  },
  titleUnderline: {
    width: "60px",
    height: "4px",
    backgroundColor: "#1e40af",
    margin: "0 auto",
    borderRadius: "2px",
  },
  text: {
    fontSize: "1.1rem",
    color: "#475569",
    textAlign: "center",
    maxWidth: "800px",
    margin: "0 auto 2rem",
    lineHeight: 1.7,
  },
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "1.5rem",
    marginTop: "2rem",
  },
  featureCard: {
    textAlign: "center",
    padding: "2rem 1.5rem",
    borderRadius: "12px",
    backgroundColor: "#f8fafc",
    transition: "all 0.3s ease",
    border: "1px solid #e2e8f0",
  },
  featureIcon: {
    fontSize: "3rem",
    marginBottom: "1rem",
  },
  featureTitle: {
    fontSize: "1.25rem",
    fontWeight: "600",
    color: "#1e293b",
    marginBottom: "0.75rem",
  },
  featureText: {
    color: "#64748b",
    lineHeight: 1.6,
  },
  benefitsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "1rem",
    marginTop: "1.5rem",
  },
  benefitItem: {
    padding: "1.25rem",
    backgroundColor: "#f8fafc",
    borderRadius: "8px",
    borderLeft: "4px solid #1e40af",
    display: "flex",
    alignItems: "flex-start",
    gap: "1rem",
    transition: "all 0.3s ease",
  },
  benefitIcon: {
    fontSize: "1.25rem",
    marginTop: "0.125rem",
    flexShrink: 0,
  },
  benefitText: {
    color: "#475569",
    lineHeight: 1.6,
    fontSize: "0.95rem",
  },
  membershipTypes: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "1.5rem",
    marginTop: "2rem",
  },
  membershipCard: {
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
    transition: "all 0.3s ease",
    border: "1px solid #e2e8f0",
  },
  membershipHeader: {
    padding: "2rem 1.5rem",
    color: "white",
    textAlign: "center",
  },
  membershipIcon: {
    fontSize: "2.5rem",
    marginBottom: "1rem",
  },
  membershipTitle: {
    margin: 0,
    fontSize: "1.5rem",
    fontWeight: "700",
  },
  membershipBody: {
    padding: "1.5rem",
    backgroundColor: "white",
  },
  membershipDetail: {
    marginBottom: "1rem",
    color: "#475569",
    lineHeight: 1.6,
    fontSize: "0.95rem",
  },
  membershipHighlight: {
    marginTop: "1rem",
    padding: "0.75rem 1rem",
    backgroundColor: "#f1f5f9",
    borderRadius: "6px",
    color: "#64748b",
    fontSize: "0.875rem",
    fontWeight: "600",
    textAlign: "center",
  },
  formSection: {
    backgroundColor: "white",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
    marginBottom: "2rem",
  },
  formHeader: {
    background: "linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)",
    color: "white",
    padding: "2rem 1.5rem",
  },
  formHeaderContent: {
    maxWidth: "1000px",
    margin: "0 auto",
    textAlign: "center",
  },
  formMainTitle: {
    fontSize: "clamp(1.5rem, 3vw, 2rem)",
    fontWeight: "700",
    marginBottom: "0.5rem",
  },
  formSubtitle: {
    fontSize: "1rem",
    opacity: 0.9,
  },
  formContainer: {
    padding: "0",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "1.5rem",
    padding: "1.5rem",
  },
  formSectionCard: {
    backgroundColor: "#f8fafc",
    padding: "1.5rem",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
  },
  sectionHeaderWithIcon: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: "1.5rem",
    gap: "1rem",
  },
  sectionIcon: {
    fontSize: "1.5rem",
    width: "48px",
    height: "48px",
    backgroundColor: "#1e40af",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    flexShrink: 0,
  },
  formSectionTitle: {
    fontSize: "1.25rem",
    fontWeight: "600",
    color: "#1e293b",
    margin: "0 0 0.25rem 0",
  },
  formSectionSubtitle: {
    fontSize: "0.95rem",
    color: "#64748b",
    margin: "0",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "1rem",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontWeight: "600",
    marginBottom: "0.5rem",
    color: "#374151",
    fontSize: "0.875rem",
  },
  inputContainer: {
    position: "relative",
  },
  input: {
    padding: "0.75rem 0.75rem 0.75rem 2.5rem",
    fontSize: "1rem",
    border: "2px solid #e2e8f0",
    borderRadius: "6px",
    transition: "all 0.2s ease",
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: "white",
    fontFamily: "inherit",
  },
  select: {
    padding: "0.75rem 0.75rem 0.75rem 2.5rem",
    fontSize: "1rem",
    border: "2px solid #e2e8f0",
    borderRadius: "6px",
    backgroundColor: "white",
    transition: "all 0.2s ease",
    width: "100%",
    boxSizing: "border-box",
    appearance: "none",
    fontFamily: "inherit",
  },
  inputIcon: {
    position: "absolute",
    left: "0.75rem",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "1rem",
    color: "#64748b",
    pointerEvents: "none",
  },
  inputError: {
    borderColor: "#ef4444",
    backgroundColor: "#fef2f2",
  },
  inputSuccess: {
    borderColor: "#10b981",
    backgroundColor: "#f0fdf4",
  },
  qualificationRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr auto",
    gap: "1rem",
    alignItems: "end",
    marginBottom: "1rem",
  },
  qualificationGroup: {
    display: "flex",
    flexDirection: "column",
  },
  addButton: {
    backgroundColor: "transparent",
    color: "#1e40af",
    border: "2px dashed #1e40af",
    padding: "0.75rem 1rem",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    width: "100%",
    marginTop: "0.5rem",
    fontSize: "0.95rem",
  },
  addButtonIcon: {
    fontSize: "1.125rem",
    marginRight: "0.5rem",
    fontWeight: "bold",
  },
  removeButton: {
    backgroundColor: "#ef4444",
    color: "white",
    border: "none",
    borderRadius: "6px",
    width: "40px",
    height: "40px",
    cursor: "pointer",
    fontSize: "1.125rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    marginBottom: "0.5rem",
  },
  termsSection: {
    backgroundColor: "white",
    padding: "1.5rem",
    borderRadius: "8px",
    border: "2px solid #e2e8f0",
  },
  termsHeader: {
    marginBottom: "1rem",
    textAlign: "center",
  },
  termsTitle: {
    fontSize: "1.125rem",
    fontWeight: "600",
    color: "#1e293b",
    marginBottom: "0.5rem",
  },
  termsSubtitle: {
    color: "#64748b",
    fontSize: "0.875rem",
  },
  termsLabel: {
    display: "flex",
    alignItems: "flex-start",
    cursor: "pointer",
    padding: "1rem",
    backgroundColor: "#f8fafc",
    borderRadius: "6px",
    border: "2px solid #e2e8f0",
    transition: "all 0.2s ease",
  },
  checkbox: {
    display: "none",
  },
  checkboxCustom: {
    width: "20px",
    height: "20px",
    border: "2px solid #d1d5db",
    borderRadius: "4px",
    marginRight: "0.75rem",
    position: "relative",
    flexShrink: 0,
    transition: "all 0.2s ease",
    marginTop: "0.125rem",
  },
  checkboxChecked: {
    backgroundColor: "#1e40af",
    borderColor: "#1e40af",
  },
  termsText: {
    fontWeight: "500",
    color: "#475569",
    lineHeight: 1.5,
    fontSize: "0.95rem",
  },
  submitSection: {
    textAlign: "center",
    marginTop: "1.5rem",
  },
  submitButton: {
    background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
    color: "white",
    border: "none",
    padding: "1rem 2rem",
    fontSize: "1rem",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 6px rgba(5, 150, 105, 0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.75rem",
    margin: "0 auto",
    minWidth: "200px",
  },
  submitButtonDisabled: {
    opacity: 0.7,
    cursor: "not-allowed",
    transform: "none",
  },
  submitButtonText: {
    fontSize: "1rem",
    fontWeight: "600",
  },
  submitButtonIcon: {
    fontSize: "1.125rem",
  },
  loadingSpinner: {
    width: "20px",
    height: "20px",
    border: "2px solid transparent",
    borderTop: "2px solid white",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  formNote: {
    fontSize: "0.875rem",
    color: "#64748b",
    marginTop: "1rem",
    maxWidth: "400px",
    margin: "1rem auto 0",
  },
};

// Add CSS animation for loading spinner
const spinnerStyle = document.createElement('style');
spinnerStyle.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(spinnerStyle);

// Enhanced hover effects for better UX
Object.assign(styles.featureCard, {
  ':hover': {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 15px rgba(0, 0, 0, 0.1)",
  }
});

Object.assign(styles.benefitItem, {
  ':hover': {
    transform: "translateX(2px)",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.08)",
  }
});

Object.assign(styles.membershipCard, {
  ':hover': {
    transform: "translateY(-3px)",
    boxShadow: "0 8px 15px rgba(0, 0, 0, 0.1)",
  }
});

Object.assign(styles.navButton, {
  ':hover': {
    color: "#1e40af",
    backgroundColor: "#f8fafc",
  }
});

Object.assign(styles.addButton, {
  ':hover': {
    backgroundColor: "#1e40af",
    color: "white",
    borderStyle: "solid",
  }
});

Object.assign(styles.submitButton, {
  ':hover': {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 12px rgba(5, 150, 105, 0.4)",
  }
});

Object.assign(styles.removeButton, {
  ':hover': {
    backgroundColor: "#dc2626",
    transform: "scale(1.05)",
  }
});

Object.assign(styles.termsLabel, {
  ':hover': {
    borderColor: "#1e40af",
    backgroundColor: "#f0f4ff",
  }
});

Object.assign(styles.input, {
  ':focus': {
    outline: "none",
    borderColor: "#1e40af",
    boxShadow: "0 0 0 3px rgba(30, 64, 175, 0.1)",
  }
});

Object.assign(styles.select, {
  ':focus': {
    outline: "none",
    borderColor: "#1e40af",
    boxShadow: "0 0 0 3px rgba(30, 64, 175, 0.1)",
  }
});

export default Membership;